import { useState, useEffect, useCallback } from 'preact/hooks'
import { apiClient, authStore, InitData, User, tenantSiteUrl, udLink } from '../../core'
import { t, intlLocale, formatMoney } from '../../core/i18n'
import { studietsTidszone } from '../../core/tidszone'

interface MembershipData {
  hasMembership: boolean
  klippekort: {
    id: string
    type: string
    totalClasses: number | null
    remainingClasses: number | null
    startDate: string
    endDate: string | null
    isPaused: boolean
    pausedUntil: string | null
    pricingPlan: {
      name: string
      description: string | null
      price: number
    } | null
  } | null
  subscription: {
    id: string
    type: string
    startDate: string
    endDate: string | null
    autoRenew: boolean
    isPaused: boolean
    pausedUntil: string | null
    pricingPlan: {
      name: string
      description: string | null
      price: number
    } | null
  } | null
  stats: {
    classesThisMonth: number
  }
  history?: Array<{
    id: string
    date: string
    planName: string
    amount: number
  }>
}

interface MembershipWidgetProps {
  initData: InitData
}

/**
 * MembershipWidget — mirrors HS-native styling.
 *
 * Auth-gated: shows logged-in user's klippekort/abonnement status.
 * Layout:
 *   - Klippekort card: balance, expiry, "Genopfyld" CTA
 *   - Abonnement card: active until, autoRenew status, "Pause / opsig" link
 *   - Purchase history (small list)
 *   - Empty state: 💳 + "Se priser" CTA → booking?tab=pricing
 *   - Logged-out: 🔐 prompt
 *   - Loading: shimmer skeleton card
 *   - Error: red-bordered alert
 */
export function MembershipWidget({ initData }: MembershipWidgetProps) {
  const str = t(initData.locale)
  const [membership, setMembership] = useState<MembershipData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [user, setUser] = useState<User | null>(authStore.getUser())

  // Subscribe to auth changes
  useEffect(() => {
    return authStore.subscribe(setUser)
  }, [])

  const fetchMembership = useCallback(async () => {
    setLoading(true)
    setError(null)

    const result = await apiClient.get<MembershipData>('/api/widget/membership', true)

    if (result.error) {
      if (result.code === 'AUTH_REQUIRED') {
        setError(null)
      } else {
        setError(result.error)
      }
    } else if (result.data) {
      setMembership(result.data)
    }

    setLoading(false)
  }, [])

  // Fetch membership when user changes
  useEffect(() => {
    if (user) {
      fetchMembership()
    } else {
      setMembership(null)
      setLoading(false)
    }
  }, [user, fetchMembership])

  // Medlemskabets datoer er tidspunkter (timestamptz): vis dem i studiets tidszone (QA 27-10).
  function formatDate(dateString: string): string {
    const date = new Date(dateString)
    return date.toLocaleDateString(intlLocale(initData.locale), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: studietsTidszone(initData.timezone),
    })
  }

  function formatAmount(amount: number): string {
    return formatMoney(amount, initData.locale, initData.currency)
  }

  const pricingHref = `${tenantSiteUrl(initData)}/booking?tab=pricing`
  const manageMembershipHref = `${tenantSiteUrl(initData)}/dashboard/medlemskab`

  // Not logged in
  if (!user) {
    return (
      <div>
        <style>{membershipStyles}</style>
        <div class="hsw-mem-empty">
          <div class="hsw-mem-empty-icon">🔐</div>
          <p class="hsw-mem-empty-text">{str.loginToSeeAgreement}</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div>
        <style>{membershipStyles}</style>
        <div class="hsw-mem-skeleton-card">
          <div class="hsw-skeleton hsw-sk-icon" />
          <div class="hsw-sk-body">
            <div class="hsw-skeleton hsw-sk-title" />
            <div class="hsw-skeleton hsw-sk-line" />
            <div class="hsw-skeleton hsw-sk-line" style={{ width: '70%' }} />
            <div class="hsw-skeleton hsw-sk-button" />
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <style>{membershipStyles}</style>
        <div class="hsw-mem-error" role="alert">
          <span class="hsw-mem-error-icon" aria-hidden="true">⚠️</span>
          <span>{error}</span>
        </div>
      </div>
    )
  }

  if (!membership || !membership.hasMembership) {
    return (
      <div>
        <style>{membershipStyles}</style>
        <div class="hsw-mem-empty">
          <div class="hsw-mem-empty-icon">💳</div>
          <p class="hsw-mem-empty-text">{str.noActiveAgreement}</p>
          <a href={pricingHref} {...udLink(pricingHref)} class="hsw-mem-cta hsw-mem-cta-primary">{str.seePrices}</a>
        </div>
      </div>
    )
  }

  const k = membership.klippekort
  const s = membership.subscription
  const history = membership.history ?? []

  const klippekortPercent =
    k && k.totalClasses && k.remainingClasses !== null
      ? Math.max(0, Math.min(100, (k.remainingClasses / k.totalClasses) * 100))
      : null

  return (
    <div>
      <style>{membershipStyles}</style>

      <div class="hsw-mem-stack">
        {/* Klippekort card */}
        {k && (
          <div class="hsw-mem-card">
            <div class="hsw-mem-card-head">
              <div class="hsw-mem-icon" aria-hidden="true">🎟️</div>
              <div class="hsw-mem-head-text">
                <div class="hsw-mem-card-title">
                  {k.pricingPlan?.name || str.klippekort}
                </div>
                <div class="hsw-mem-card-sub">
                  {str.startedOn} {formatDate(k.startDate)}
                </div>
              </div>
              {k.isPaused && <span class="hsw-mem-badge hsw-mem-badge-warn">{str.paused}</span>}
            </div>

            {k.remainingClasses !== null && (
              <div class="hsw-mem-balance">
                <span class="hsw-mem-balance-num">{k.remainingClasses}</span>
                <span class="hsw-mem-balance-label">
                  {str.clipsRemaining}{k.totalClasses ? ` ${str.ofTotal} ${k.totalClasses}` : ''}
                </span>
              </div>
            )}

            {klippekortPercent !== null && (
              <div
                class="hsw-mem-progress"
                role="progressbar"
                aria-valuenow={k.remainingClasses ?? 0}
                aria-valuemin={0}
                aria-valuemax={k.totalClasses ?? 0}
                aria-label={str.klippekort}
              >
                <div class="hsw-mem-progress-fill" style={{ width: `${klippekortPercent}%` }} />
              </div>
            )}

            {k.endDate && (
              <div class="hsw-mem-meta">{str.expiresOn} {formatDate(k.endDate)}</div>
            )}

            <a href={pricingHref} {...udLink(pricingHref)} class="hsw-mem-cta hsw-mem-cta-primary">{str.topUp}</a>
          </div>
        )}

        {/* Subscription card */}
        {s && (
          <div class="hsw-mem-card">
            <div class="hsw-mem-card-head">
              <div class="hsw-mem-icon" aria-hidden="true">📅</div>
              <div class="hsw-mem-head-text">
                <div class="hsw-mem-card-title">
                  {s.pricingPlan?.name || str.subscriptionTitle}
                </div>
                <div class="hsw-mem-card-sub">
                  {s.type === 'yearly' ? str.yearly : str.monthly} {str.subscription}
                </div>
              </div>
              {s.isPaused ? (
                <span class="hsw-mem-badge hsw-mem-badge-warn">{str.paused}</span>
              ) : (
                <span class="hsw-mem-badge hsw-mem-badge-ok">{str.active}</span>
              )}
            </div>

            {s.endDate && (
              <div class="hsw-mem-meta hsw-mem-meta-strong">
                {str.activeUntil} {formatDate(s.endDate)}
              </div>
            )}

            <div class="hsw-mem-meta">
              {s.autoRenew
                ? `✓ ${str.autoRenews}`
                : str.noAutoRenew}
            </div>

            <a href={manageMembershipHref} {...udLink(manageMembershipHref)} class="hsw-mem-cta hsw-mem-cta-link">
              {str.pauseOrCancel}
            </a>
          </div>
        )}

        {/* Stats */}
        <div class="hsw-mem-stat-card">
          <div class="hsw-mem-stat-num">{membership.stats.classesThisMonth}</div>
          <div class="hsw-mem-stat-label">{str.classesThisMonth}</div>
        </div>

        {/* Purchase history */}
        {history.length > 0 && (
          <div class="hsw-mem-card">
            <div class="hsw-mem-section-title">{str.previousPurchases}</div>
            <ul class="hsw-mem-history">
              {history.map(item => (
                <li key={item.id} class="hsw-mem-history-row">
                  <span class="hsw-mem-history-date">{formatDate(item.date)}</span>
                  <span class="hsw-mem-history-plan">{item.planName}</span>
                  <span class="hsw-mem-history-amount">{formatAmount(item.amount)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

const membershipStyles = `
  .hsw-mem-stack {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    max-width: 640px;
    margin: 0 auto;
  }
  .hsw-mem-card {
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    padding: 1.75rem;
    transition: all .3s;
    display: flex;
    flex-direction: column;
  }
  .hsw-mem-card:hover {
    border-color: rgba(0,0,0,0.2);
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  }
  .hsw-mem-card-head {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    margin-bottom: 1.25rem;
  }
  .hsw-mem-icon {
    font-size: 1.75rem;
    line-height: 1;
    flex-shrink: 0;
  }
  .hsw-mem-head-text {
    flex: 1;
    min-width: 0;
  }
  .hsw-mem-card-title {
    font-size: 1.15rem;
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    letter-spacing: -0.01em;
    line-height: 1.2;
  }
  .hsw-mem-card-sub {
    font-size: 0.85rem;
    color: var(--yoga-text-muted, #737373);
    margin-top: 0.15rem;
  }
  .hsw-mem-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 99px;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    white-space: nowrap;
  }
  .hsw-mem-badge-ok {
    background: #ecfdf5;
    color: #047857;
  }
  .hsw-mem-badge-warn {
    background: #fef3c7;
    color: #92400e;
  }
  .hsw-mem-balance {
    display: flex;
    align-items: baseline;
    gap: 0.6rem;
    margin: 0.5rem 0 1rem;
    line-height: 1;
  }
  .hsw-mem-balance-num {
    font-size: 3rem;
    font-weight: 800;
    color: var(--yoga-primary, #FF6B35);
    line-height: 1;
    letter-spacing: -0.04em;
  }
  .hsw-mem-balance-label {
    font-size: 0.95rem;
    color: var(--yoga-text-muted, #525252);
    font-weight: 500;
  }
  .hsw-mem-progress {
    height: 8px;
    background: var(--yoga-bg-secondary, #f5f5f5);
    border-radius: 99px;
    overflow: hidden;
    margin-bottom: 1rem;
  }
  .hsw-mem-progress-fill {
    height: 100%;
    background: var(--yoga-primary, #FF6B35);
    border-radius: 99px;
    transition: width .4s ease;
  }
  .hsw-mem-meta {
    font-size: 0.9rem;
    color: var(--yoga-text-muted, #737373);
    margin: 0.25rem 0;
    line-height: 1.5;
  }
  .hsw-mem-meta-strong {
    color: var(--yoga-text, #0a0a0a);
    font-weight: 500;
  }
  .hsw-mem-cta {
    display: block;
    margin-top: 1.25rem;
    padding: 0.85rem 1.25rem;
    text-align: center;
    font-weight: 500;
    border-radius: var(--yoga-radius, 0);
    text-decoration: none;
    font-size: 0.9rem;
    letter-spacing: 0.01em;
    transition: all .3s;
  }
  .hsw-mem-cta-primary {
    background: var(--yoga-primary, #FF6B35);
    color: #fff;
    border: 1px solid var(--yoga-primary, #FF6B35);
  }
  .hsw-mem-cta-primary:hover {
    background: var(--yoga-primary-900, #1A1A2E);
    border-color: var(--yoga-primary-900, #1A1A2E);
  }
  .hsw-mem-cta-link {
    background: transparent;
    color: var(--yoga-text, #0a0a0a);
    border: 1px solid #d4d4d4;
  }
  .hsw-mem-cta-link:hover {
    background: var(--yoga-text, #0a0a0a);
    color: var(--yoga-card-bg, #fff);
    border-color: var(--yoga-text, #0a0a0a);
  }
  .hsw-mem-stat-card {
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    padding: 1.5rem;
    text-align: center;
  }
  .hsw-mem-stat-num {
    font-size: 2.25rem;
    font-weight: 800;
    color: var(--yoga-primary, #FF6B35);
    line-height: 1;
    letter-spacing: -0.03em;
  }
  .hsw-mem-stat-label {
    font-size: 0.85rem;
    color: var(--yoga-text-muted, #737373);
    margin-top: 0.4rem;
  }
  .hsw-mem-section-title {
    font-size: 0.9rem;
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 1rem;
  }
  .hsw-mem-history {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
  }
  .hsw-mem-history-row {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 0.85rem;
    align-items: center;
    padding: 0.7rem 0;
    border-bottom: 1px solid var(--yoga-card-border, #f0f0f0);
    font-size: 0.9rem;
  }
  .hsw-mem-history-row:last-child { border-bottom: none; }
  .hsw-mem-history-date {
    color: var(--yoga-text-muted, #737373);
    font-variant-numeric: tabular-nums;
  }
  .hsw-mem-history-plan {
    color: var(--yoga-text, #0a0a0a);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .hsw-mem-history-amount {
    color: var(--yoga-text, #0a0a0a);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .hsw-mem-empty {
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    padding: 3rem 2rem;
    text-align: center;
    max-width: 640px;
    margin: 0 auto;
  }
  .hsw-mem-empty-icon {
    font-size: 2.75rem;
    line-height: 1;
    margin-bottom: 1rem;
  }
  .hsw-mem-empty-text {
    color: var(--yoga-text, #0a0a0a);
    font-size: 1rem;
    margin: 0 0 1.25rem;
  }
  .hsw-mem-empty .hsw-mem-cta {
    display: inline-block;
    margin-top: 0;
    padding: 0.85rem 1.75rem;
  }
  .hsw-mem-error {
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: var(--yoga-radius, 0);
    padding: 1rem 1.25rem;
    color: #b91c1c;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    max-width: 640px;
    margin: 0 auto;
  }
  .hsw-mem-error-icon { font-size: 1.1rem; line-height: 1; }
  .hsw-mem-skeleton-card {
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    padding: 1.75rem;
    display: flex;
    gap: 1rem;
    max-width: 640px;
    margin: 0 auto;
  }
  .hsw-skeleton {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: hsw-mem-shimmer 1.5s infinite;
    border-radius: 4px;
  }
  .hsw-sk-icon { width: 48px; height: 48px; border-radius: 50%; flex-shrink: 0; }
  .hsw-sk-body { flex: 1; display: flex; flex-direction: column; gap: 0.6rem; }
  .hsw-sk-title { height: 22px; width: 55%; }
  .hsw-sk-line { height: 14px; width: 100%; }
  .hsw-sk-button { height: 42px; width: 100%; margin-top: 0.75rem; }
  @keyframes hsw-mem-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`
